import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { CustomerDeliveryAddress, DeliveryApiException, CustomerProfile } from '@/models';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { AccountManagementEvent, FindAChipotleClickEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';
import { getCountryCodeByTopLevelDomain } from '@/helpers';

@Component({
  name: 'CmgAddressValidationResult',
})
export default class CmgAddressValidationResult extends Vue {
  @Prop({ type: Object })
  selectedSearchResult?: QueryAutocompletePrediction;

  @Prop({ type: Object })
  selectedDeliveryAddress!: CustomerDeliveryAddress;

  @Prop({ type: String })
  iconUrl?: string;

  @Prop({ type: String })
  switchToPickup?: string;

  @Prop({ type: Boolean })
  isValidDeliveryAddress?: boolean;

  @Prop({ type: Boolean })
  canDeliverToSelectedCountry?: boolean;

  get buttonText(): string {
    return !this.isValidDeliveryAddress ? 'try another address' : this.selectedDeliveryAddress.name == null ? 'add address details' : 'deliver to saved address';
  }

  get closeButtonImageUrl(): string {
    return this.$appStore.state.cms.images.buttons.xDarkBrown.default;
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  get formattedAddressLine1() {
    if (!this.selectedSearchResult) {
      return;
    }
    return this.selectedSearchResult.structured_formatting.main_text;
  }

  get formattedAddressLine2() {
    if (!this.selectedSearchResult) {
      return;
    }
    return this.selectedSearchResult.structured_formatting.secondary_text;
  }

  get mealIds(): string {
    return (!this.selectedDeliveryAddress || this.selectedDeliveryAddress.name == null) ? 'add-delivery-add' : '';
  }

  closeValidationResultView() {
    this.$emit('close-address-validation-result', true);
  }

  showPickupView() {
    this.$emit('show-pickup-view', true);
    this.$services.analytics.send(new AccountManagementEvent(InteractionTrackingType.PickupInstead));
  }
  mounted() {
    Vue.nextTick(() => {
      setTimeout(() => {
        const focusValidaddress = document.getElementsByClassName('luck-valid-address')[0] as HTMLElement;
        const focusNotValidaddress = document.getElementsByClassName('not-valid-address')[0] as HTMLElement;
        focusValidaddress.focus();
        if (focusNotValidaddress) {
          focusNotValidaddress.focus();
        }
      }, 2000);
    });
  }
  async onClickDeliveryAddressButton(event: any) {
    const isSavedAddress = !!this.selectedDeliveryAddress.name;

    if (this.isValidDeliveryAddress && isSavedAddress) {
      this.$emit('set-is-loading', true);
      //if this is a saved address, revalidate and select it

      let validationResult;

      try {
        validationResult = await this.$services.order.validateDeliveryAddress(
          this.$services.findAChipotle.customerDeliveryAddressToValidationRequest(this.selectedDeliveryAddress),
        );
      } catch (e) {
        this.$emit('set-is-loading', false);
        this.$services.ui.setModalVisibility('findAChipotle', false);
        this.$services.ui.setModalVisibility('basicError', true, {
          header: `Sorry we can't deliver to this address`,
          content: `Try again or switch to pickup.`,
          options: [
            {
              text: 'Okay',
              type: ButtonType.Primary,
              onClick: () => {
                this.$services.ui.setModalVisibility('basicError', false);
                this.$services.ui.setModalVisibility('findAChipotle', true);
              },
            },
          ],
        });
        this.$services.ui.firstFocus();
      }

      if (validationResult && !(validationResult instanceof DeliveryApiException) && validationResult.statusText === 'OK') {
        // If we were able to get a restaurantId we can use that to set the current restaurant
        const restaurantId = validationResult.data.restaurantId;
        const restaurant = await this.$services.restaurant.getRestaurantByNumber(restaurantId);

        await this.$services.order.setDeliveryAddress(this.selectedDeliveryAddress);

        try {
          await this.$services.restaurant.selectRestaurant(restaurant, false);
        } catch (e) {
          this.$emit('set-is-loading', false);
          this.$emit('show-delivery-address-validation-result', false);
          return;
        }
        

        this.$emit('set-is-loading', false);
        this.$services.ui.setModalVisibility('findAChipotle', false);

        if(getCountryCodeByTopLevelDomain() === restaurant.address.countryCode) {
          this.$services.ui.setModalVisibility('toast', true, {
            title: 'DELIVERING TO',
            subTitle: this.selectedDeliveryAddress.name ? this.selectedDeliveryAddress.name.toUpperCase() : undefined,
            body: this.selectedDeliveryAddress.streetAddress1!,
            legalCopy: 'Menu pricing for delivery is higher and fees apply.',
            icon: this.$appStore.state.cms.images.icons.carWhite.default,
            after: async () => {
              const epops = await this.$services.epop.getEpopsForRestaurant(restaurantId);
              if (epops && epops.length > 0) {
                this.$services.ui.setModalVisibility('epop', true, {
                  epops,
                });
                this.$services.ui.firstFocus();
              }
            },
          });
        }

        this.$services.ui.focusBack();

        // Plop some values into store so we can carry over saved address-specific data to checkout
        this.$services.checkout.setDeliveryPhoneNumber(this.selectedDeliveryAddress.phoneNumber);
        this.$services.checkout.setDeliveryName(this.selectedDeliveryAddress.customerName);
        this.$services.checkout.setDeliveryInstructions(this.selectedDeliveryAddress.notes || '');
      } else {
        this.$emit('set-is-loading', false);
      }

    } else if (this.isValidDeliveryAddress && !isSavedAddress) {
      //if this is a new address, Add Address Details
      this.$services.ui.pushEventTargetAsLastSelectedElement(event);
      this.$services.ui.setModalVisibility('deliveryAddress', true, {
        isSavedAddress: false,
        deliveryAddress: this.selectedDeliveryAddress,
      });
      this.$services.ui.firstFocus();      
    } else {
      this.closeValidationResultView();
    }

    this.$services.analytics.send(new FindAChipotleClickEvent(isSavedAddress ? 'addSavedAddressDetails' : 'addAddressDetails'));
  }
}
